import HttpClient from '../http/HttpClient';
import { LEAD_SERVICE_HOST, LEAD_API_PREFIX } from '../constants/RestConstants';

export interface DebitInterestBreakdownRow {
  debitId: string;
  principalAmount: number;
  outstandingAmount: number;
  monthlyInterest: number;
  interestRatePercent: number;
  paymentDay: number;
  maturityDate?: string;
  lastInterestCollectedDate?: string;
  cycleStartDate?: string;
  cycleAnchorSource?: string;
  elapsedDays: number;
  perDayInterest: number;
  interestTillToday: number;
  status: string;
  debitType: string;
  formulaDescription?: string;
}

export interface CustomerInterestOutstandingReport {
  borrowerId: string;
  borrowerName: string;
  asOfDate: string;
  totalDebitAmount: number;
  totalOutstanding: number;
  totalExpectedMonthlyInterest: number;
  totalPendingInterestTillToday: number;
  nearestMaturityDate?: string;
  activeInterestRatesLabel: string;
  debits: DebitInterestBreakdownRow[];
  shareTextEnglish?: string;
  shareTextTamil?: string;
}

export interface InterestCollectionPreview {
  debitId: string;
  outstandingAmount: number;
  interestRatePercent: number;
  monthlyInterest: number;
  perDayInterest: number;
  lastInterestCollectedDate?: string;
  cycleStartDate?: string;
  cycleAnchorSource?: string;
  collectionDate: string;
  elapsedDays: number;
  collectibleAmount: number;
  formulaUsed: string;
  explanation: string;
}

export interface PaymentDayChangePreview {
  debitId: string;
  existingPaymentDay: number;
  newPaymentDay: number;
  dayDifference: number;
  asOfDate: string;
  currentPendingInterest: number;
  projectedPendingInterest: number;
  pendingInterestDelta: number;
  currentCycleStartDate?: string;
  projectedCycleStartDate?: string;
  currentElapsedDays: number;
  projectedElapsedDays: number;
  cycleImpactSummary: string;
  partialAdjustmentNote: string;
  requiresConfirmation: boolean;
}

function unwrap<T>(response: any): T {
  const body = response?.data ?? response;
  return body?.data ?? body;
}

export async function getInterestOutstandingReport(
  borrowerId: string,
  asOfDate?: string
): Promise<CustomerInterestOutstandingReport> {
  const params: Record<string, string> = {};
  if (asOfDate) params.asOfDate = asOfDate;
  const response = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/borrowers/${borrowerId}/interest-outstanding-report`,
    params,
  });
  return unwrap<CustomerInterestOutstandingReport>(response);
}

export async function previewInterestCollection(
  debitId: string,
  asOfDate?: string
): Promise<InterestCollectionPreview> {
  const params: Record<string, string> = {};
  if (asOfDate) params.asOfDate = asOfDate;
  const response = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/debits/${debitId}/interest-collection-preview`,
    params,
  });
  return unwrap<InterestCollectionPreview>(response);
}

export async function collectInterest(
  debitId: string,
  payload: {
    collectionDate?: string;
    amount?: number;
    note?: string;
    paymentReceivedBy?: string;
    creditScore?: number;
  } = {}
) {
  const response = await HttpClient.POST({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/debits/${debitId}/collect-interest`,
    data: payload,
  });
  return unwrap(response);
}

export async function previewPaymentDayChange(
  debitId: string,
  newPaymentDay: number,
  asOfDate?: string
): Promise<PaymentDayChangePreview> {
  const params: Record<string, string | number> = { newPaymentDay };
  if (asOfDate) params.asOfDate = asOfDate;
  const response = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/debits/${debitId}/payment-day-change-preview`,
    params,
  });
  return unwrap<PaymentDayChangePreview>(response);
}

export async function updatePaymentDay(debitId: string, paymentDay: number) {
  const response = await HttpClient.PATCH({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/debits/${debitId}/payment-day`,
    data: { paymentDay },
  });
  return unwrap(response);
}
